/**
 * Package Detail Page
 * 
 * Shows full details for a specific dispute package including:
 * - Package metadata
 * - Findings included
 * - Full letter preview
 * - Download and export options
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { metro2Service, ROUND_STATUS_INFO } from '../../services/metro2Service';
import { formatTimestamp } from '../../utils/dateTime';
import {
  ArrowLeft,
  FileText,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Printer,
  Copy,
  Building2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700',
  Experian: 'bg-amber-100 text-amber-700',
  Equifax: 'bg-red-100 text-red-700',
};

const PackageDetailPage = () => {
  const { roundId, packageId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [round, setRound] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [letterContent, setLetterContent] = useState(null);
  
  useEffect(() => {
    if (roundId && packageId) {
      loadPackageData();
    }
  }, [roundId, packageId]);
  
  const loadPackageData = async () => {
    setLoading(true);
    try {
      // Load round data
      const roundResult = await metro2Service.getRound(roundId, token);
      setRound(roundResult.round);
      
      // Find the package
      const foundPkg = roundResult.packages?.find(p => p.id === packageId);
      setPkg(foundPkg);
      
      // Load letter content if available
      if (foundPkg?.letter_url) {
        try {
          const letterResult = await metro2Service.getLetter(roundId, packageId, token);
          setLetterContent(letterResult);
        } catch (error) {
          console.error('Failed to load letter:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load package:', error);
      toast.error('Failed to load package details');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownload = () => {
    if (!letterContent?.content) {
      toast.error('Letter content not available');
      return;
    }
    
    const blob = new Blob([letterContent.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_letter_${pkg?.target_bureau || 'package'}_${roundId.slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Letter downloaded');
  };
  
  const handlePrint = () => {
    if (!letterContent?.content) {
      toast.error('Letter content not available');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(letterContent.content);
    printWindow.document.close();
    printWindow.print();
  };
  
  const handleCopy = () => {
    if (!letterContent?.content) {
      toast.error('Letter content not available');
      return;
    }
    
    // Create a temporary element to extract text
    const temp = document.createElement('div');
    temp.innerHTML = letterContent.content;
    const textContent = temp.textContent || temp.innerText;
    
    navigator.clipboard.writeText(textContent);
    toast.success('Letter text copied to clipboard');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  
  if (!pkg) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center">
        <AlertCircle size={48} className="text-stone-400 mb-4" />
        <h2 className="text-xl font-semibold text-stone-900 mb-2">Package Not Found</h2>
        <p className="text-stone-600 mb-4">The dispute package you're looking for doesn't exist.</p>
        <Button onClick={() => navigate(`/disputes/${roundId}`)}>
          <ArrowLeft size={18} className="mr-2" />
          Back to Round
        </Button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-stone-50" data-testid="package-detail-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Button variant="ghost" className="mb-4" onClick={() => navigate(`/disputes/${roundId}`)}>
            <ArrowLeft size={18} className="mr-2" />
            Back to Round
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-stone-900">
                  Dispute Package
                </h1>
                {pkg.target_bureau ? (
                  <Badge className={BUREAU_COLORS[pkg.target_bureau]}>{pkg.target_bureau}</Badge>
                ) : (
                  <Badge variant="outline">{pkg.package_type}</Badge>
                )}
                {pkg.letter_url ? (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle size={12} className="mr-1" />
                    Letter Ready
                  </Badge>
                ) : (
                  <Badge className="bg-stone-100 text-stone-600">
                    <Clock size={12} className="mr-1" />
                    Pending
                  </Badge>
                )}
              </div>
              <p className="text-stone-500 font-mono text-sm">{pkg.id}</p>
            </div>
            
            {pkg.letter_url && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCopy}>
                  <Copy size={18} className="mr-2" />
                  Copy
                </Button>
                <Button variant="outline" onClick={handlePrint}>
                  <Printer size={18} className="mr-2" />
                  Print
                </Button>
                <Button onClick={handleDownload} className="btn-primary">
                  <Download size={18} className="mr-2" />
                  Download
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Package Info + Findings */}
          <div className="space-y-6">
            {/* Package Metadata */}
            <div className="bg-white border rounded-xl p-6" data-testid="package-metadata">
              <h2 className="font-semibold text-stone-900 mb-4">Package Details</h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Type</p>
                  <p className="font-medium text-stone-900">{pkg.package_type}</p>
                </div>
                {pkg.target_bureau && (
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Target Bureau</p>
                    <p className="font-medium text-stone-900 flex items-center gap-2">
                      <Building2 size={16} className="text-stone-400" />
                      {pkg.target_bureau}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Template</p>
                  <p className="font-medium text-stone-900 capitalize">
                    {pkg.template_family || 'Standard'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Findings</p>
                  <p className="font-medium text-stone-900">{pkg.findings_snapshot?.length || 0}</p>
                </div>
                {pkg.letter_content_hash && (
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Content Hash</p>
                    <p className="font-mono text-xs text-stone-600 truncate">{pkg.letter_content_hash}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Generated</p>
                  <p className="font-medium text-stone-900">
                    {formatTimestamp(pkg.generated_at)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Findings in Package */}
            <div className="bg-white border rounded-xl p-6" data-testid="package-findings">
              <h2 className="font-semibold text-stone-900 mb-4">
                Included Findings ({pkg.findings_snapshot?.length || 0})
              </h2>
              
              {pkg.findings_snapshot?.length === 0 ? (
                <div className="text-center py-6 text-stone-500">
                  <AlertCircle size={24} className="mx-auto mb-2 opacity-50" />
                  <p>No findings in this package</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pkg.findings_snapshot?.map((finding, index) => (
                    <div
                      key={finding.id || index}
                      className="border rounded-lg p-3"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileText size={14} className="text-stone-400" />
                        <span className="font-medium text-stone-900 text-sm">{finding.rule_name}</span>
                      </div>
                      <p className="text-xs text-stone-600">{finding.description}</p>
                      {finding.metro_field_codes?.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {finding.metro_field_codes.map(code => (
                            <Badge key={code} variant="outline" className="text-xs font-mono">
                              {code}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Right: Letter Content */}
          <div className="lg:col-span-2">
            <div className="bg-white border rounded-xl p-6" data-testid="letter-content">
              <h2 className="font-semibold text-stone-900 mb-4">Dispute Letter</h2>
              
              {letterContent?.content ? (
                <div 
                  className="border rounded-lg p-6 bg-white prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: letterContent.content }}
                />
              ) : (
                <div className="text-center py-16 text-stone-500 border rounded-lg bg-stone-50">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">Letter Not Generated</p>
                  <p className="text-sm mt-2">Generate letters for this round to view the content.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetailPage;
